export const MAX_FILE_BYTES = 100 * 1024 * 1024;

export const attributeKey = value => JSON.stringify(value == null ? null : value);

export function numericValue(value) {
  if (!['number', 'string'].includes(typeof value) || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function numericFields(collection) {
  const fields = new Set();
  collection.features.forEach(feature => {
    Object.entries(feature.properties || {}).forEach(([name, value]) => {
      if (numericValue(value) !== null) fields.add(name);
    });
  });
  return Array.from(fields).sort();
}

export function attributeFields(collection) {
  const fields = new Set();
  collection.features.forEach(feature => {
    Object.entries(feature.properties || {}).forEach(([name, value]) => {
      if (value == null || ['string', 'number', 'boolean'].includes(typeof value)) fields.add(name);
    });
  });
  return Array.from(fields).sort();
}

export function featureMatches(feature, filters) {
  return filters.every(filter => !filter.field || filter.values.has(attributeKey(feature.getProperty(filter.field))));
}

export async function readMapFile(file) {
  if (file.size > MAX_FILE_BYTES) throw new Error('tooLarge');
  if (/\.zip$/i.test(file.name)) {
    let parsed;
    try {
      // Use the browser bundle with the project's Webpack 4 build.
      const module = await import('shpjs/dist/shp');
      const parse = module.default || module;
      parsed = await parse(await file.arrayBuffer());
    } catch (error) {
      throw new Error('invalidShapefile');
    }
    const collections = Array.isArray(parsed) ? parsed : [parsed];
    if (!collections.length) throw new Error('emptyGeoJson');
    return collections.map((collection, index) => ({
      name: collection?.fileName || `${file.name.replace(/\.zip$/i, '')}${collections.length > 1 ? ` (${index + 1})` : ''}`,
      collection: normalizeGeoJson(collection),
    }));
  }
  if (!/\.(geojson|json)$/i.test(file.name)) throw new Error('unsupportedFile');
  let json;
  try {
    json = JSON.parse((await file.text()).replace(/^\uFEFF/, ''));
  } catch (error) {
    throw new Error('invalidGeoJson');
  }
  return [{ name: file.name, collection: normalizeGeoJson(json) }];
}

export function normalizeGeoJson(value) {
  let positions = 0;
  const invalid = () => { throw new Error('invalidGeoJson'); };
  const position = coordinates => {
    if (!Array.isArray(coordinates) || coordinates.length < 2
      || !coordinates.every(number => typeof number === 'number' && Number.isFinite(number))
      || Math.abs(coordinates[0]) > 180 || Math.abs(coordinates[1]) > 90) invalid();
    positions += 1;
  };
  const sequence = (coordinates, minimum, validate) => {
    if (!Array.isArray(coordinates) || coordinates.length < minimum) invalid();
    coordinates.forEach(validate);
  };
  const line = coordinates => sequence(coordinates, 2, position);
  const ring = coordinates => {
    sequence(coordinates, 4, position);
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (first.length !== last.length || first.some((number, index) => number !== last[index])) invalid();
  };
  const polygon = coordinates => sequence(coordinates, 1, ring);
  const geometry = (item, depth = 0) => {
    if (!item || typeof item !== 'object' || depth > 32) invalid();
    switch (item.type) {
      case 'Point': position(item.coordinates); break;
      case 'MultiPoint': sequence(item.coordinates, 1, position); break;
      case 'LineString': line(item.coordinates); break;
      case 'MultiLineString': sequence(item.coordinates, 1, line); break;
      case 'Polygon': polygon(item.coordinates); break;
      case 'MultiPolygon': sequence(item.coordinates, 1, polygon); break;
      case 'GeometryCollection':
        sequence(item.geometries, 1, child => geometry(child, depth + 1));
        break;
      default: invalid();
    }
  };
  const ids = new Set();
  const feature = item => {
    if (!item || item.type !== 'Feature'
      || (item.properties != null && (typeof item.properties !== 'object' || Array.isArray(item.properties)))
      || (item.id != null && !['string', 'number'].includes(typeof item.id))) invalid();
    if (item.id != null) {
      if (ids.has(String(item.id))) invalid();
      ids.add(String(item.id));
    }
    if (item.geometry !== null) geometry(item.geometry);
  };
  if (!value || typeof value !== 'object') invalid();
  if (value.crs && !['urn:ogc:def:crs:OGC:1.3:CRS84', 'urn:ogc:def:crs:EPSG::4326', 'EPSG:4326']
    .includes(value.crs.properties?.name)) invalid();
  let collection;
  if (value.type === 'FeatureCollection') {
    sequence(value.features, 1, feature);
    collection = value;
  } else if (value.type === 'Feature') {
    feature(value);
    collection = { type: 'FeatureCollection', features: [value] };
  } else {
    geometry(value);
    collection = { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: value }] };
  }
  if (!positions) throw new Error('emptyGeoJson');
  return collection;
}
